import { useIsUserRole } from "@/app/utils/auth";
import {
  ArrowRightCircleIcon,
  LockClosedIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  PlusCircleIcon,
} from "@heroicons/react/16/solid";
import { CourseUserDetailsDTO } from "@tumtutor/shared/dto";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ButtonWithTooltip } from "../../components/buttonWithTooltip";
import { openModal } from "../../components/modal";
import { closeModal } from "../../components/modal";
import { useToastEffect } from "../../hooks";
import { useEnrollCourseMutation } from "../../services/courseApi";
import { buildQueryString } from "../../utils/api.util";
import { getLecturerDisplayName } from "../../utils/utils";
import { ENROLL_COURSE_MODAL_ID } from "./HomePage";
import { SEARCH_MODAL_ID } from "./SearchCoursesModal";
import { CourseInfo } from "./components/EnrollCourseMask";

interface CourseCardProps {
  allCourses: boolean;
  course: CourseUserDetailsDTO;
  setCourseToEnroll?: (course: CourseInfo) => void;
  setSearchQuery?: (query: string) => void;
  setClickedCourse?: (course: CourseInfo) => void;
}

function CourseCard(props: CourseCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(["courseCard", "toast"]);
  const isAdmin = useIsUserRole("admin");
  const [enrollCourse, { isError, isSuccess, error }] =
    useEnrollCourseMutation();

  useToastEffect({
    isError,
    isSuccess,
    error,
    successMessage: t("courseCard.successEnroll", {
      courseName: props.course.name,
      ns: "toast",
    }),
  });

  const openCourse = () => {
    navigate(`course/${props.course.id}`);
  };

  const editCourse = () => {
    const path = buildQueryString(`course/${props.course.id}/manage`, {
      edit: true,
    });
    navigate(path);
  };

  const courseHandler = () => {
    if (props.course.isEnrolled) {
      props.setClickedCourse?.({
        id: props.course.id,
        name: props.course.name,
      });
      openModal("leave_modal");
    } else {
      if (props.course.isPasswordProtected) {
        props.setCourseToEnroll?.({
          id: props.course.id,
          name: props.course.name,
        });
        openModal(ENROLL_COURSE_MODAL_ID);
      } else {
        enrollCourse({ courseId: props.course.id });
        closeModal(SEARCH_MODAL_ID, () => props.setSearchQuery?.(""));
      }
    }
  };

  return (
    <div
      className={`card my-1 flex h-auto min-h-14 flex-row flex-wrap items-center justify-between rounded-xl bg-neutral py-1 pl-4 shadow-sm ${props.allCourses ? "" : "cursor-pointer hover:bg-opacity-75 hover:shadow-lg"}`}
      onClick={props.allCourses ? undefined : openCourse}
      onKeyDown={
        props.allCourses
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                openCourse();
              }
            }
      }
    >
      <div className="flex-1">
        <h2 className="w-4/5 font-semibold text-md sm:text-lg">
          {props.course.name}
        </h2>
        <div>{getLecturerDisplayName(props.course.lecturer)}</div>
      </div>
      <div className="flex flex-shrink-0 flex-row items-center">
        {props.course.isPasswordProtected && !props.course.isEnrolled && (
          <div className="tooltip" data-tip={t("tooltipPassword")}>
            <LockClosedIcon className="h-7 w-7" />
          </div>
        )}
        {props.allCourses ? (
          <>
            <div
              className=" badge mt-1 ml-sm whitespace-nowrap text-primary-content "
              style={{ backgroundColor: props.course.university?.color }}
            >
              {props.course.university?.shortName}
            </div>
            {props.course.isEnrolled ? (
              <ButtonWithTooltip
                icon={<MinusCircleIcon className="h-7 w-7" />}
                tooltip={t("leave")}
                handler={courseHandler}
              />
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-primary mx-4 hidden sm:inline"
                  onClick={courseHandler}
                >
                  {t("enroll")}
                </button>
                <div className="inline sm:hidden">
                  <ButtonWithTooltip
                    icon={<PlusCircleIcon className="h-7 w-7" />}
                    tooltip={t("enroll")}
                    handler={courseHandler}
                  />
                </div>
              </>
            )}
            {isAdmin && (
              <>
                <button
                  type="button"
                  onClick={editCourse}
                  className="btn btn-ghost btn-circle"
                >
                  <PencilSquareIcon className="h-7 w-7" />
                </button>
                <button
                  type="button"
                  onClick={openCourse}
                  className="btn btn-ghost btn-circle"
                >
                  <ArrowRightCircleIcon className="h-7 w-7" />
                </button>
              </>
            )}
          </>
        ) : props.course.isManaging || isAdmin ? (
          <ButtonWithTooltip
            icon={<PencilSquareIcon className="h-7 w-7" />}
            tooltip={t("edit")}
            handler={editCourse}
          />
        ) : (
          <ButtonWithTooltip
            icon={<ArrowRightCircleIcon className="h-7 w-7" />}
            tooltip={t("goToCourse")}
            handler={openCourse}
          />
        )}
      </div>
    </div>
  );
}

export default CourseCard;
